/**
 * Business Processes List Route
 *
 * Displays searchable, filterable list of business processes
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useBusinessProcesses } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { BusinessProcess } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/business-processes/')({
  component: BusinessProcessesListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { BusinessProcessesListPage };

interface BusinessProcessFilters {
  search: string;
  processType: string;
  processTier: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

function BusinessProcessesListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<BusinessProcessFilters>({
    search: '',
    processType: '',
    processTier: '',
  });

  // Fetch business processes with GraphQL
  const { data, loading, error } = useBusinessProcesses();

  // Extract business processes from connection
  const businessProcesses = data?.businessProcesses?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<BusinessProcess>({
      items: businessProcesses,
      page,
      pageSize,
      filterFn: (businessProcess) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = businessProcess.name.toLowerCase().includes(searchLower);
          const matchesDescription = businessProcess.description
            ?.toLowerCase()
            .includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        if (filters.processType && businessProcess.processType !== filters.processType) {
          return false;
        }
        if (filters.processTier && businessProcess.processTier !== filters.processTier) {
          return false;
        }
        return true;
      },
      sortValue: (businessProcess, column) =>
        String(businessProcess[column as keyof BusinessProcess] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<BusinessProcess>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '35%',
      render: (businessProcess) => (
        <div>
          <div className="entity-list-name-cell">{businessProcess.name}</div>
          {businessProcess.description && (
            <div className="entity-list-description-cell">
              {businessProcess.description.substring(0, 60)}
              {businessProcess.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'processType',
      label: 'Process Type',
      sortable: true,
      width: '20%',
      render: (businessProcess) => <span>{businessProcess.processType || '—'}</span>,
    },
    {
      key: 'processTier',
      label: 'Process Tier',
      sortable: true,
      width: '20%',
      render: (businessProcess) => <span>{businessProcess.processTier || '—'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '25%',
      render: (businessProcess) => <span>{businessProcess.status || '—'}</span>,
    },
  ];

  const handleRowClick = (businessProcess: BusinessProcess): void => {
    navigate({ to: `/business-processes/${businessProcess.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<BusinessProcessFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/business-processes', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '', processType: '', processTier: '' });
    void navigate({ to: '/business-processes', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/business-processes', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({
      to: '/business-processes',
      search: { page: 1, pageSize: newPageSize },
    });
  };

  if (error) {
    return (
      <div className="business-processes-list-page">
        <h1 className="forge-typography--headline4">Business Processes</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load business processes"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="business-processes-list-page">
      <div className="business-processes-header">
        <div>
          <h1 className="forge-typography--headline4">Business Processes</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} business processes`}
          </p>
        </div>
        <PermissionGate permission="business-process:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/business-processes/new' })}
          >
            Create Business Process
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search business processes..."
            aria-label="Search business processes"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        <select
          className="filter-select"
          aria-label="Filter by process type"
          value={filters.processType}
          onChange={(e) => handleFilterChange({ processType: e.target.value })}
        >
          <option value="">All Process Types</option>
          <option value="core">Core</option>
          <option value="supporting">Supporting</option>
          <option value="management">Management</option>
        </select>

        <select
          className="filter-select"
          aria-label="Filter by process tier"
          value={filters.processTier}
          onChange={(e) => handleFilterChange({ processTier: e.target.value })}
        >
          <option value="">All Process Tiers</option>
          <option value="strategic">Strategic</option>
          <option value="operational">Operational</option>
          <option value="tactical">Tactical</option>
        </select>

        {(filters.search || filters.processType || filters.processTier) && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="business-processes-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={4} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(businessProcess) => businessProcess.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No business processes found',
                description:
                  filters.search || filters.processType || filters.processTier
                    ? 'Try adjusting your filters'
                    : 'Create your first business process to get started',
                actionLabel: 'Create Business Process',
                onAction: () => navigate({ to: '/business-processes/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
